import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, AlertTriangle, MapPin, Eye, EyeOff, Globe, Copy } from "lucide-react";
import { toast } from "sonner";
import { fmtShowDate, LinkedTag, isProvisional } from "@/components/shows/showsUtils";
import { todayIso } from "@/components/calendar/calendarUtils";
import { EventDialog } from "./EventDialog";

// Stato reale verso allievi e pubblico (stesse regole del server)
const StatusBadges = ({ ev }) => {
  const prov = isProvisional(ev);
  const future = (ev.performances || []).some((p) => p.date >= todayIso());
  const out = [];
  if (prov) out.push(["provisional", "Titolo provvisorio", "bg-amber-500/15 text-amber-800", null]);
  if (ev.visible && !prov && future) out.push(["visible", "Visibile agli allievi", "bg-green-500/15 text-green-700", <Eye key="i" size={12} className="mr-1" />]);
  else if (!ev.visible || prov) out.push(["hidden", "Nascosto", "bg-zinc-500/15 text-zinc-600", <EyeOff key="i" size={12} className="mr-1" />]);
  else out.push(["past", "Concluso: non più visibile", "bg-zinc-500/15 text-zinc-600", null]);
  if (ev.public && !prov && future && (!ev.linked_course || ev.visible)) out.push(["public", "Pubblico", "bg-sky-500/15 text-sky-800", <Globe key="i" size={12} className="mr-1" />]);
  return out.map(([k, l, c, icon]) => <Badge key={k} className={`border-0 ${c}`} data-testid={`event-status-${k}-${ev.id}`}>{icon}{l}</Badge>);
};

const EventCard = ({ ev, onEdit, onDelete }) => (
  <li className="rounded-xl border border-border bg-white p-4 space-y-2" data-testid={`event-${ev.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-lg font-semibold break-words">{ev.title}</p>
        {ev.location && <p className="text-sm text-muted-foreground flex items-center gap-1 break-words"><MapPin size={14} className="shrink-0" /> {ev.location}</p>}
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="icon" variant="ghost" className="h-12 w-12 sm:h-10 sm:w-10" onClick={onEdit} data-testid={`event-edit-${ev.id}`} aria-label="Modifica"><Pencil size={16} /></Button>
        {!ev.linked_course && <Button size="icon" variant="ghost" className="h-12 w-12 sm:h-10 sm:w-10" onClick={onDelete} data-testid={`event-delete-${ev.id}`} aria-label="Elimina"><Trash2 size={16} /></Button>}
      </div>
    </div>
    <div className="flex flex-wrap gap-2">
      {ev.linked_course && <LinkedTag ev={ev} testid={`event-linked-${ev.id}`} />}
      <StatusBadges ev={ev} />
      {ev.poster_url && <Badge className="border-0 bg-muted text-foreground" data-testid={`event-has-poster-${ev.id}`}>Locandina</Badge>}
      <Badge className="border-0 bg-muted text-foreground break-words whitespace-normal text-left" data-testid={`event-scope-label-${ev.id}`}>{ev.scope === "all" ? "Tutta la scuola" : `Corsi: ${ev.courses.join(", ") || "nessuno"}`}</Badge>
    </div>
    <ul className="space-y-1 text-sm">
      {ev.performances.map((p) => (
        <li key={p.id} data-testid={`event-perf-row-${p.id}`}>
          <span className="font-semibold first-letter:uppercase">{fmtShowDate(p.date, p.time)} {p.date.slice(0, 4)}</span>{p.link ? " · biglietti online" : ""}
          {p.date < todayIso() && <span className="text-xs text-muted-foreground" data-testid={`event-perf-past-${p.id}`}> · passata</span>}
          {p.closure && <span className="ml-1 inline-flex items-center gap-1 text-amber-700" data-testid={`event-closure-${p.id}`}><AlertTriangle size={13} /> giorno di chiusura ({p.closure})</span>}
        </li>
      ))}
    </ul>
  </li>
);

// Link della pagina pubblica (anno corrente, solo eventi "Pagina pubblica")
const PublicLink = () => {
  const url = `${window.location.origin}/spettacoli`;
  const copy = () => navigator.clipboard?.writeText(url).then(() => toast.success("Link copiato")).catch(() => toast.error("Copia non riuscita"));
  return (
    <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm space-y-2" data-testid="public-shows-link">
      <p>Pagina pubblica degli spettacoli dell'anno corrente (solo eventi con "Pagina pubblica" attivo, senza dati degli allievi):</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <a href={url} target="_blank" rel="noopener noreferrer" className="break-all text-primary underline underline-offset-2 self-center" data-testid="public-shows-url">{url}</a>
        <Button size="sm" variant="outline" onClick={copy} data-testid="public-shows-copy" className="shrink-0"><Copy size={14} className="mr-1" /> Copia link</Button>
      </div>
    </div>
  );
};

// Saggi con data già inserita ma senza spettacolo collegato (creazione su richiesta del master, idempotente)
const MissingRecitals = ({ year, onDone }) => {
  const [missing, setMissing] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/events/recitals-missing", { params: { year } }).then((r) => setMissing(r.data)).catch(() => setMissing([]));
  useEffect(() => { load(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!missing.length) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/events/link-recitals", null, { params: { year } });
      toast.success(`Creati ${data.created} spettacoli dei saggi (nascosti)`); load(); onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="recitals-missing">
      <p>{missing.length} saggi hanno già una data ma non ancora uno spettacolo collegato. Verranno creati nascosti, con titolo provvisorio e ora da definire.</p>
      <Button size="sm" onClick={run} disabled={busy} data-testid="recitals-missing-create">Crea gli spettacoli dei saggi</Button>
    </div>
  );
};

// Tab "Spettacoli" del master (anno di lavoro)
export const EventsManager = ({ year }) => {
  const [events, setEvents] = useState(null);
  const [courses, setCourses] = useState([]);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/events", { params: { year } }).then((r) => setEvents(r.data)).catch(() => setEvents([]));
  useEffect(() => {
    setEvents(null); load();
    api.get("/admin/year-config", { params: { year } }).then((r) => setCourses(r.data.courses || [])).catch(() => {});
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (f) => {
    setSaving(true);
    const body = { title: f.title, description: f.description, location: f.location, scope: f.scope, courses: f.courses, visible: f.visible,
      public: !!f.public, poster_url: (f.poster_url || "").trim(),
      performances: f.performances.map((p) => ({ ...(p.id ? { id: p.id } : {}), date: p.date, time: p.time, link: (p.link || "").trim() })) };
    try {
      if (edit.ev) await api.put(`/admin/events/${edit.ev.id}`, body, { params: { year } });
      else await api.post("/admin/events", body, { params: { year } });
      toast.success("Evento salvato"); setEdit(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/events/${del.id}`, { params: { year } }); toast.success("Evento eliminato"); setDel(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="p-5 sm:p-6 bg-card border-border space-y-4" data-testid="events-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="text-xl">Spettacoli {year}</h3>
          <p className="text-sm text-muted-foreground">Eventi della scuola. Ogni data di saggio impostata in "Anni e corsi" crea qui uno spettacolo collegato, nascosto finché non lo rendi visibile.</p></div>
        <Button onClick={() => setEdit({ ev: null })} data-testid="event-new"><Plus size={16} className="mr-1" /> Nuovo evento</Button>
      </div>
      <PublicLink />
      <MissingRecitals year={year} onDone={load} />
      {events === null ? <p className="text-sm text-muted-foreground">Caricamento…</p>
        : events.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="events-empty">Nessun evento per quest'anno.</p>
          : <ul className="space-y-3" data-testid="events-list">{events.map((ev) => <EventCard key={ev.id} ev={ev} onEdit={() => setEdit({ ev })} onDelete={() => setDel(ev)} />)}</ul>}
      {edit && <EventDialog open onOpenChange={(o) => !o && setEdit(null)} initial={edit.ev} courses={courses} onSave={save} saving={saving} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="w-[calc(100%-1.5rem)] rounded-lg">
          <AlertDialogHeader><AlertDialogTitle>Eliminare "{del?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>L'evento e tutte le sue repliche spariranno anche per gli allievi.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={remove} data-testid="event-delete-confirm">Elimina</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
